/**
 * PitfallDetailPanel — queue #666 §UAT-2026-07-09-R666 §R666-4.
 *
 * 함정 사전에서 함정 선택 시 상세 — 증상/원인/해결 3구획(i18n) + 해결 코드/설정 스니펫
 * (있으면 코드블록 + `CopyButton` §R638 재사용) + 관련 모듈 코드 배지(클릭 시 호출부가
 * 카탈로그 뷰로 전환 + 해당 노드 선택) + 출처 참조(있으면 표시).
 */
import { useTranslation } from 'react-i18next';
import { CopyButton, StatusPill } from '@ui-kit';
import type { Pitfall } from '../data/pitfalls';
import { severityTone } from './PitfallCard';

const blockStyle: React.CSSProperties = {
  marginBottom: 'var(--spacing-4)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
};

const blockHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 'var(--spacing-2)',
};

const blockTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: 0,
};

const bodyTextStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  margin: 0,
  lineHeight: 1.6,
};

const preStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-xs)',
  background: 'var(--color-surface-muted)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--spacing-3)',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.6,
  margin: 0,
};

interface Props {
  pitfall: Pitfall;
  onBack: () => void;
  /** 관련 모듈 배지 클릭 시 — 카탈로그 뷰로 전환 + 해당 노드 선택 */
  onSelectModule: (code: string) => void;
}

export function PitfallDetailPanel({ pitfall, onBack, onSelectModule }: Props) {
  const { t } = useTranslation();

  return (
    <section aria-label={t(pitfall.titleKey)}>
      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--color-brand-primary)',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          padding: 0,
          marginBottom: 'var(--spacing-3)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        {t('showcase.pitfall.detail.back')}
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        <h2
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          {t(pitfall.titleKey)}
        </h2>
        <StatusPill tone={severityTone(pitfall.severity)}>
          {t(`showcase.pitfall.severity.${pitfall.severity}`)}
        </StatusPill>
      </div>

      {/* 증상 */}
      <div style={blockStyle}>
        <p style={blockTitleStyle}>{t('showcase.pitfall.detail.symptom')}</p>
        <p style={{ ...bodyTextStyle, marginTop: 'var(--spacing-2)' }}>{t(pitfall.symptomKey)}</p>
      </div>

      {/* 원인 */}
      <div style={blockStyle}>
        <p style={blockTitleStyle}>{t('showcase.pitfall.detail.cause')}</p>
        <p style={{ ...bodyTextStyle, marginTop: 'var(--spacing-2)' }}>{t(pitfall.causeKey)}</p>
      </div>

      {/* 해결 */}
      <div style={blockStyle}>
        <div style={blockHeaderStyle}>
          <p style={blockTitleStyle}>{t('showcase.pitfall.detail.fix')}</p>
          {pitfall.snippet && (
            <CopyButton
              text={pitfall.snippet}
              ariaLabel={t('showcase.takeThis.copyAria', { block: t('showcase.pitfall.detail.fix') })}
              copyLabel={t('showcase.takeThis.copy')}
              copiedLabel={t('showcase.takeThis.copied')}
            />
          )}
        </div>
        <p style={bodyTextStyle}>{t(pitfall.fixKey)}</p>
        {pitfall.snippet && <pre style={{ ...preStyle, marginTop: 'var(--spacing-2)' }}>{pitfall.snippet}</pre>}
      </div>

      {/* 관련 모듈 */}
      <div style={blockStyle}>
        <p style={blockTitleStyle}>{t('showcase.pitfall.card.related')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)' }}>
          {pitfall.moduleCodes.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => onSelectModule(code)}
              aria-label={t('showcase.recipe.detail.moduleJumpAria', { code })}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
                fontSize: 'var(--font-size-xs)',
                background: 'var(--color-surface-background)',
                border: '1px solid var(--color-brand-primary)',
                color: 'var(--color-brand-primary)',
                borderRadius: 'var(--radius-sm)',
                padding: '2px var(--spacing-2)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              {code}
            </button>
          ))}
        </div>
      </div>

      {/* 출처 */}
      {pitfall.ref && (
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            margin: 0,
          }}
        >
          {t('showcase.pitfall.detail.ref')}: <code style={{ fontFamily: 'var(--font-family-mono)' }}>{pitfall.ref}</code>
        </p>
      )}
    </section>
  );
}
